import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';

import { Container, Grid, Stack, Tab, Tabs } from '@mui/material';

import { productTabs } from '@/features/authentication/features/subscription/data/product-tabs';
import {
  ProductDetailsType,
  useSubscriptionProducts,
} from '@/features/authentication/features/subscription/store';

import { PRODUCT_TABS } from '../../constants';
import { Subscription } from '../../types';
import { ProductCard } from '../ProductCard';

type SubscriptionData = (Subscription | null)[];

interface ModifyProps {
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  initialSubscriptions?: SubscriptionData;
  onSubscriptionChange?: (subscriptions: SubscriptionData) => void;
}

export const Modify = ({
  activeTab,
  setActiveTab,
  initialSubscriptions = [],
  onSubscriptionChange,
}: ModifyProps) => {
  const products = useSubscriptionProducts();

  const [subscriptions, setSubscriptions] = useState<SubscriptionData>(() => {
    const tabCount = productTabs.length;
    const initialSubs: SubscriptionData = Array(tabCount).fill(
      null,
    ) as SubscriptionData;

    if (initialSubscriptions.length > 0) {
      initialSubscriptions.forEach((sub) => {
        if (sub) {
          for (let tabIndex = 0; tabIndex < tabCount; tabIndex++) {
            const tabProducts = products[tabIndex] || [];
            const matchingProduct = tabProducts.find(
              (p) =>
                p.product_id === String(sub.product_id) &&
                p.tier_id === String(sub.tier_id),
            );
            if (matchingProduct) {
              initialSubs[tabIndex] = sub;
              break;
            }
          }
        }
      });
    }

    return initialSubs;
  });

  useEffect(() => {
    if (initialSubscriptions.length > 0) {
      const tabCount = productTabs.length;
      const updatedSubs: SubscriptionData = Array(tabCount).fill(
        null,
      ) as SubscriptionData;

      initialSubscriptions.forEach((sub) => {
        if (sub) {
          for (let tabIndex = 0; tabIndex < tabCount; tabIndex++) {
            const tabProducts = products[tabIndex] || [];
            const matchingProduct = tabProducts.find(
              (p) =>
                p.product_id === String(sub.product_id) &&
                p.tier_id === String(sub.tier_id),
            );
            if (matchingProduct) {
              updatedSubs[tabIndex] = sub;
              break;
            }
          }
        }
      });

      setSubscriptions(updatedSubs);
    }
  }, [initialSubscriptions, products]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number): void => {
    setActiveTab(newValue);
  };

  const handleProductSelectionChange = (subscription: Subscription | null) => {
    const updatedSubscriptions = [...subscriptions];

    updatedSubscriptions[activeTab] = subscription;

    setSubscriptions(updatedSubscriptions);
    onSubscriptionChange?.(updatedSubscriptions);
  };

  const renderStandardProductCard = (
    item: ProductDetailsType,
    index: number,
  ) => (
    <Grid item xs={12} sm={6} md={4} key={`grid-${index}`}>
      <ProductCard
        active={activeTab}
        {...item}
        length={products[activeTab]?.length || 0}
        selectedSubscription={subscriptions[activeTab]}
        onSelectionChange={handleProductSelectionChange}
      />
    </Grid>
  );

  const renderSpecialCaseProductCards = () => {
    return products[activeTab]?.map((item, index) => {
      if (index < 2) {
        return renderStandardProductCard(item, index);
      }

      if (index === 2) {
        const enterprisePro = products[activeTab].find(
          (p) => p.tier_id === '9',
        );
        const enterpriseAdvanced = products[activeTab].find(
          (p) => p.tier_id === '2',
        );

        return (
          <Grid item xs={12} sm={6} md={4} key={`grid-${index}`}>
            <Stack spacing={4}>
              {enterprisePro && (
                <ProductCard
                  active={activeTab}
                  {...enterprisePro}
                  length={products[activeTab].length}
                  key="card-enterprise-pro"
                  selectedSubscription={subscriptions[activeTab]}
                  onSelectionChange={handleProductSelectionChange}
                />
              )}
              {enterpriseAdvanced && (
                <ProductCard
                  active={activeTab}
                  {...enterpriseAdvanced}
                  length={products[activeTab].length}
                  key="card-enterprise-advanced"
                  selectedSubscription={subscriptions[activeTab]}
                  onSelectionChange={handleProductSelectionChange}
                />
              )}
            </Stack>
          </Grid>
        );
      }

      return null;
    });
  };

  const renderProductCards = () => {
    if (activeTab === 2) {
      return renderSpecialCaseProductCards();
    }

    return products[activeTab]?.map((item, index) =>
      renderStandardProductCard(item, index),
    );
  };

  return (
    <Stack alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        TabIndicatorProps={{ sx: { height: 5 } }}
        aria-label="Product subscription options"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ maxWidth: '100%' }}
      >
        {PRODUCT_TABS.map((tab, index) => (
          <Tab
            key={`tab-${index}`}
            label={tab.label}
            id={`product-tab-${index}`}
            aria-controls={`product-tabpanel-${index}`}
          />
        ))}
      </Tabs>

      <Container maxWidth="lg" disableGutters>
        <Grid
          container
          spacing={2}
          sx={{ padding: 2, width: '100%', margin: 0 }}
          justifyContent="center"
        >
          {renderProductCards()}
        </Grid>
      </Container>
    </Stack>
  );
};
